"use client";
import React, { useState } from "react";
import {
  Box,
  Button,
  Container,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
  FormHelperText,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import SendIcon from "@mui/icons-material/SendRounded";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import type { Country } from "react-phone-number-input";
import { getPhoneNumberLib } from "@/utility/lazyPhoneNumber";
import { subscribeToNewsLetter } from "@/services/backOffice";
import countriesList from "@/assets/json/countycode.json";
import Flag from "@/components/common/CountryFlag";
import OtpNewslater from "./OtpNewslater";
import { AxiosError } from "axios";
import CircularProgress from "@mui/material/CircularProgress";

interface IMembersOnly {
  emailAddress: string;
  firstName: string;
  lastName: string;
  mobileNumber: string;
  country?: string;
}

const Schema = yup.object().shape({
  firstName: yup.string().required("First Name is Required"),
  lastName: yup.string().required("Last Name is Required"),
  emailAddress: yup.string().email("Must be a valid email").max(50).required("Email is Required"),
  mobileNumber: yup
    .string()
    .required("Phone Number is a required")
    .test("phone", "Enter valid Number", async function (value) {
      if (!value) return false;
      const country = this.parent.country;
      const { isPossiblePhoneNumber } = await getPhoneNumberLib();
      return isPossiblePhoneNumber(value, country as Country);
    }),
});

const NewsLetter = () => {
  const [showAllField, setShowAllField] = useState<boolean>(false);
  const [isOtp, setIsOtp] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    handleSubmit,
    formState: { errors },
    control,
    watch,
    reset,
    register,
  } = useForm<IMembersOnly>({
    defaultValues: {
      firstName: "",
      lastName: "",
      emailAddress: "",
    },
    resolver: yupResolver(Schema),
    mode: "onSubmit",
  });

  const values = watch();

  const [loading, setLoading] = useState(false);
  const submitEnquiry = async (): Promise<void> => {
    const { parsePhoneNumber } = await getPhoneNumberLib();
    const phoneNumber = parsePhoneNumber(values.mobileNumber!, values.country as Country);
    const ContactNo = phoneNumber?.number || "";

    try {
      setLoading(true);
      await subscribeToNewsLetter({
        email: values.emailAddress,
        firstName: values.firstName,
        lastName: values.lastName,
        mobile: ContactNo,
      });
      setIsOtp(true);
      setError(null);
      setLoading(false);
    } catch (err) {
      const error = err as AxiosError<{ detail?: string }>;
      if (error.response) {
        const errorMessage = error.response.data?.detail;
        setError(errorMessage || "An unexpected error occurred.");
      } else {
        setError("Something went wrong. Please try again.");
      }
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (!errors.emailAddress && values?.emailAddress) setShowAllField(true);
    else setShowAllField(false);
  }, [values, errors]);

  return (
    <Container>
      <Box sx={{ mt: "1.5rem" }}>
        <Typography
          variant="h2"
          sx={{
            fontSize: { xs: "2.2rem", md: "3rem" },
            fontWeight: { xs: 800, md: 700 },
            // textAlign: "center",
            color: "primary.main",
            lineHeight: { xs: 1.1, md: 1.3 },
            mb: { xs: "10px", md: "0px" },
            span: {
              color: "transparent",
              WebkitTextStrokeColor: "#CE0000",
              WebkitTextStrokeWidth: "1px",
            },
          }}
        >
          Be First. Be VIP. <span>Be Onboard. </span>
        </Typography>
        <Box
          sx={{
            fontSize: { xs: "14px", md: "1rem" },
            fontWeight: 400,
            lineHeight: { xs: 1.4, md: 1.3 },
            // textAlign: "center",
            mb: { xs: "1.6rem", md: "2.2rem" },
          }}
        >
          Subscribe to our newsletter for exclusive cruise deals, new itineraries, ship updates, and
          insider travel tips – straight to your inbox.
        </Box>
      </Box>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: "900px",
          mb: { xs: "2rem", md: "3rem" },
        }}
      >
        {!isOtp && (
          <Box
            component="form"
            onSubmit={handleSubmit(submitEnquiry)}
            sx={{
              width: { xs: "100%", md: showAllField ? "75vw" : "100%" },
            }}
          >
            <Grid
              container
              maxWidth={"xl"}
              spacing={{ xs: 1.5, md: 2 }}
              columnSpacing={{ xs: 1, md: 2 }}
            >
              <Grid size={{ xs: 12, sm: showAllField ? 6 : 8, md: showAllField ? 4 : 9 }}>
                <Controller
                  control={control}
                  name={"emailAddress"}
                  render={({ field, fieldState: { error, invalid } }) => (
                    <>
                      {" "}
                      <TextField
                        label="Email address"
                        placeholder="Enter your email address"
                        variant="outlined"
                        fullWidth
                        focused
                        type="email"
                        {...field}
                        error={invalid}
                      />
                      <FormHelperText sx={{ pl: "0.9rem" }} error>
                        {error?.message}
                      </FormHelperText>
                    </>
                  )}
                />
              </Grid>
              {showAllField && (
                <>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Controller
                      control={control}
                      name={"firstName"}
                      render={({ field, fieldState: { error, invalid } }) => (
                        <>
                          <TextField
                            // sx={{ height: "3rem" }}
                            variant="outlined"
                            label="First name"
                            {...field}
                            error={invalid}
                            placeholder="Enter your firstname"
                            fullWidth
                          />
                          <FormHelperText sx={{ pl: "0.9rem" }} error>
                            {error?.message}
                          </FormHelperText>
                        </>
                      )}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Controller
                      control={control}
                      name={"lastName"}
                      render={({ field, fieldState: { error, invalid } }) => (
                        <>
                          <TextField
                            // variant="filled"
                            variant="outlined"
                            label="Last name"
                            {...field}
                            error={invalid}
                            placeholder="Enter your lastname"
                            fullWidth
                          />
                          <FormHelperText sx={{ pl: "0.9rem" }} error>
                            {error?.message}
                          </FormHelperText>
                        </>
                      )}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Controller
                      control={control}
                      name={"mobileNumber"}
                      render={({ field, fieldState: { error, invalid } }) => (
                        <>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              position: "relative",
                            }}
                          >
                            <TextField
                              label=""
                              aria-label="Country code"
                              variant="outlined"
                              fullWidth
                              size="small"
                              select
                              focused
                              defaultValue="GB"
                              {...register("country")}
                              SelectProps={{
                                renderValue: () => {
                                  return (
                                    <Box
                                      sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        "& > img": {
                                          height: "25px",
                                          width: "40px",
                                        },
                                      }}
                                    >
                                      <Flag code={values?.country ?? "GB"} height="25" />
                                    </Box>
                                  );
                                },
                                MenuProps: {
                                  anchorOrigin: {
                                    vertical: "bottom",
                                    horizontal: "left",
                                  },
                                  transformOrigin: {
                                    vertical: "top",
                                    horizontal: "left",
                                  },
                                  sx: {
                                    maxHeight: "400px",
                                  },
                                },
                              }}
                              sx={{
                                position: "absolute",
                                zIndex: 11,
                                width: "80px",
                                top: "2px",
                                left: "0px",
                                border: "none",
                                ".MuiOutlinedInput-notchedOutline": {
                                  border: "none",
                                },
                                ".MuiSelect-select.MuiSelect-outlined.MuiInputBase-input.MuiOutlinedInput-input":
                                  {
                                    p: "14px 10px",
                                    pr: "0px",
                                  },
                                ".MuiSvgIcon-root.MuiSvgIcon-fontSizeMedium.MuiSelect-icon.MuiSelect-iconOutlined":
                                  {
                                    right: "7px",
                                  },
                              }}
                            >
                              {countriesList?.map((option, key) => (
                                <MenuItem value={option.value} key={key}>
                                  <Box
                                    component="li"
                                    sx={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      "& > img": {
                                        mr: "4px",
                                        height: "25px",
                                        width: "40px",
                                      },
                                      height: "1.4375em !important",
                                    }}
                                  >
                                    <Flag code={option.value} height="30" />
                                    <Box sx={{ ml: "0.5rem" }}>
                                      {option.label} ({option.dial_code})
                                    </Box>
                                  </Box>
                                </MenuItem>
                              ))}
                            </TextField>

                            <TextField
                              {...field}
                              label="Phone Number"
                              variant="outlined"
                              fullWidth
                              {...register("mobileNumber")}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Box sx={{ mr: "-5px", mt: "3px" }}>
                                      {countriesList.find(
                                        (country) => country.value === values?.country,
                                      )?.dial_code || "+44"}
                                    </Box>
                                  </InputAdornment>
                                ),
                              }}
                              sx={{
                                position: "relative",
                                zIndex: 10,
                                ".MuiInputBase-root.MuiOutlinedInput-root.MuiInputBase-fullWidth.MuiInputBase-formControl.MuiInputBase-adornedStart":
                                  {
                                    pl: "74px",
                                  },
                                ".MuiFormLabel-root.MuiInputLabel-root.MuiInputLabel-shrink.MuiInputLabel-sizeSmall.MuiInputLabel-shrink.MuiInputLabel-outlined":
                                  {
                                    top: "-1px",
                                  },
                                ".MuiInputBase-input.MuiOutlinedInput-input.MuiInputBase-inputSizeSmall.MuiInputBase-inputAdornedStart":
                                  {
                                    p: "8.5px 3px 8px",
                                    ml: "-3px",
                                  },
                              }}
                              color="secondary"
                              error={invalid}
                            />
                          </Box>
                          <FormHelperText sx={{ pl: "0.9rem" }} error>
                            {error?.message}
                          </FormHelperText>
                        </>
                      )}
                    />
                  </Grid>
                </>
              )}

              <Grid size={{ xs: 12, sm: showAllField ? 6 : 4, md: showAllField ? 3 : 3 }}>
                <Button
                  variant="contained"
                  disableElevation
                  fullWidth
                  type="submit"
                  sx={{
                    height: "100%",
                    width: "100%",
                    gap: "8px",
                    display: "flex",
                    textAlign: "left",
                    lineHeight: 1.2,
                    minHeight: { xs: "48px", md: "48px" },
                    maxHeight: "56px",
                  }}
                >
                  {loading ? (
                    <CircularProgress sx={{ color: "#fff" }} size={20} />
                  ) : (
                    <>
                      Subscribe
                      <SendIcon />
                    </>
                  )}
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}
        {error && <Box sx={{ color: "red", fontSize: "14px", p: "8px" }}>{error}</Box>}

        {isOtp && (
          <OtpNewslater
            emailAddress={values.emailAddress}
            firstName={values.firstName}
            lastName={values.lastName}
            mobileNumber={values.mobileNumber}
            status={true}
            onBack={() => setIsOtp(false)}
            onSubmit={() => {
              setIsOtp(false);
              reset();
            }}
          />
        )}
      </Box>
    </Container>
  );
};

export default NewsLetter;
